import { Iterable, Map, Record } from 'immutable';

const isMap = (value) => Map.isMap(value);
const isRecord = (value) => value instanceof Record;

/**
 * Checks if value is a JS object (and isn't an Immutable collection)
 * @param value
 * @returns {boolean}
 */
const isJSObject = (value) => (
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !Iterable.isIterable(value)
);

/**
 * Universally get an element from JS object / Map / Record
 * @param {Object|Immutable.Map|Immutable.Record} collection - The JS object / Map / Record
 * @param {string} key - The key to get value of
 */
const getValue = (collection, key) => {
  if (collection && typeof collection.get === 'function') {
    return collection.get(key);
  }
  return collection[key];
};

/**
 * Universally set value for a key in JS object / Map / Record
 * @param {Object|Immutable.Map|Immutable.Record} collection - The JS object / Map / Record
 * @param {string} key - The key to set value of
 * @param {any} value - The value
 * @returns {Object|Immutable.Map|Immutable.Record}
 */
const setValue = (collection, key, value) => {
  if (collection && typeof collection.get === 'function') {
    return collection.set(key, value);
  }
  collection[key] = value; // eslint-disable-line no-param-reassign
  return collection;
};

/**
 * Universally return all keys in JS object / Map / Record
 * @param {Object|Immutable.Map|Immutable.Record} collection - The JS object / Map / Record
 * @returns {Array}
 */
const getKeys = (collection) => {
  if (isMap(collection)) {
    return collection.keySeq().toArray();
  }
  return isJSObject(collection) ? Object.keys(collection) : [];
};

const isPrimitive = (obj) => (
  obj === null ||
  ['number', 'string', 'boolean', 'bigint', 'undefined', 'symbol'].includes(typeof obj)
);

export const createMerge = (overrideFunction) => (oldState, newState) => {
  // If the state is non-mergeable, we want to return early
  if (
    // Shortcut early if previous state or new state is a primitive / cannot be merged directly
    isPrimitive(oldState) ||
    isPrimitive(newState) ||
    !isJSObject(oldState) ||
    isMap(newState)
  ) {
    return newState;
  }

  let hasChanged = false;
  const nextState = {};
  const usedKeys = new Set();

  getKeys(newState).forEach((key) => {
    const oldValue = getValue(oldState, key);
    const newValue = getValue(newState, key);
    const nextValue = overrideFunction(oldValue, newValue);
    setValue(nextState, key, nextValue);
    usedKeys.add(key);
    hasChanged = hasChanged || nextValue !== oldValue;
  });

  if (hasChanged) {
    getKeys(oldState).forEach((key) => {
      if (!usedKeys.has(key)) {
        setValue(nextState, key, getValue(oldState, key));
      }
    });

    return isMap(newState || oldState) ? Map(nextState) : nextState; // check oldState instead?
  }

  return oldState;
};

/**
 * Shallowly merges 2 values into one
 * @param oldValue
 * @param newValue
 * @returns The merged value
 */
const shallowMerge = createMerge((_oldValue, newValue) => newValue);
/**
 * Deeply merges 2 values into one
 * @param oldValue
 * @param newValue
 * @returns The merged value
 */
const deepMerge = createMerge((oldValue, newValue) => deepMerge(oldValue, newValue));

// https://github.com/ioof-holdings/redux-dynostore/tree/master/packages/redux-dynostore-core#statehandler
const deepStateHandler = {
  canMerge: isJSObject,
  createEmpty: () => ({}),
  getKeys,
  getValue,
  merge: (oldState, newState) => {
    if (isJSObject(newState.apps)) {
      return {
        ...shallowMerge(oldState, newState),
        apps: deepMerge(oldState.apps, newState.apps),
      };
    }
    return shallowMerge(oldState, newState);
  },
  setValue,
};

/**
 * Filters out actions with no types, and removes the payload from redux actions.
 * @param {import('@saucelabs/sl-util-redux').FluxAction|undefined} action
 */
const backtraceActionFilter = (action) => {
  if (!action || !action.type) {
    return undefined;
  }

  const { type } = action;

  return {
    type,
  };
};

export {
  deepMerge,
  deepStateHandler,
  getKeys,
  getValue,
  isJSObject,
  isMap,
  isRecord,
  setValue,
  shallowMerge,
  backtraceActionFilter,
};
export default deepStateHandler;
